import React, { Component } from 'react';
import { DateTime } from 'luxon';
import tinycolor from 'tinycolor2';
import { css } from 'glamor';
import { library } from '@fortawesome/fontawesome-svg-core';
import { faSquare } from '@fortawesome/free-solid-svg-icons/faSquare';
import { faCheckSquare } from '@fortawesome/free-solid-svg-icons/faCheckSquare';
import { faCircle } from '@fortawesome/free-solid-svg-icons/faCircle';
import { faDotCircle } from '@fortawesome/free-solid-svg-icons/faDotCircle';
import { faTable } from '@fortawesome/free-solid-svg-icons/faTable';
import { faClock } from '@fortawesome/free-solid-svg-icons/faClock';
import { faFilter } from '@fortawesome/free-solid-svg-icons/faFilter';
import { faTimes } from '@fortawesome/free-solid-svg-icons/faTimes';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons/faChevronDown';
import { faChevronUp } from '@fortawesome/free-solid-svg-icons/faChevronUp';
import { faChevronLeft } from '@fortawesome/free-solid-svg-icons/faChevronLeft';
import { faChevronRight } from '@fortawesome/free-solid-svg-icons/faChevronRight';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import ApiHelper from './helpers/ApiHelper';
import { GetSessions } from './helpers/GetSessions';
import StyleHelper from '../shared/helpers/StyleHelper';
import DayList from './components/DayList';
import SessionList from './components/SessionList';
import TimetableList from './components/TimetableList';
import ActivityFilter from './components/ActivityFilter';
import LocationFilter from './components/LocationFilter';
import TimeOfDayFilter from './components/TimeOfDayFilter';
import SportActivityLocationFilter from './components/SportActivityLocationFilter';
import { jsPDF } from 'jspdf';
import PropTypes from 'prop-types';
import '../Timetable.css';
import html2canvas from 'html2canvas';
import { accumulateStylePropertiesFromCustomStyleSections } from '../shared/utils/styleUtils';

library.add(faSquare);
library.add(faCheckSquare);
library.add(faCircle);
library.add(faDotCircle);
library.add(faTable);
library.add(faClock);
library.add(faFilter);
library.add(faTimes);
library.add(faChevronDown);
library.add(faChevronUp);
library.add(faChevronLeft);
library.add(faChevronRight);

/**
 * @typedef {{
 *   activity: string;
 *   provider: string;
 * } & import('./Config').Config & Record<string, any>} Props
 */

/**
 * @typedef State
 * @prop {string} date
 * @prop {TimetableFilter} filter
 * @prop {any} location
 * @prop {import('./helpers/SessionHelper').SearchApiEventSeries[]} sessions
 * @prop {boolean} hasLoadedActivityList
 * @prop {boolean} hasLoadedSessions
 * @prop {string} listType
 * @prop {any} overrideListType
 * @prop {boolean} showFilters
 * @prop {string} loadingCaption
 * @prop {any} activityList
 * @prop {boolean} showActivityTypes
 * @prop {boolean} showPrintButton
 */

/**
 * @typedef {{
 *   activity: string[];
 *   timeOfDay: string;
 *   sportActivityLocation?: any[];
 * }} TimetableFilter
 */

/**
 * @extends Component<Props,State>
 */
class Timetable extends Component {
  /**
   * @param {Props} props
   */
  constructor(props) {
    super(props);
    this.state = {
      date: DateTime.local().toISODate(),
      filter: this.defaultFilter(this.props.activity),
      location: this.props.location || this.props.initialLocation,
      sessions: [],
      hasLoadedActivityList: false,
      hasLoadedSessions: false,
      listType: 'list',
      overrideListType: null,
      showFilters: false,
      loadingCaption: 'Loading...',
      activityList: null,
      showActivityTypes: this.props.showActivityTypes,
      showPrintButton: this.props.showPrintButton,
    };
    this.calendarMaxWidth = 650;
    this.resizeTimer = null;
    this.resizeFunction = null;
    this.height = null;
    if (typeof this.props.height === 'string') {
      let test = this.props.height.replace('px', '');
      if (test.match(/^[0-9]+$/)) {
        // convert to rem
        this.height = this.pxToRem(test);
      } else if (test.match(/^[0-9.]+rem$/)) {
        this.height = parseFloat(test.replace('rem', ''));
      }
    } else if (typeof this.props.height === 'number') {
      this.height = this.pxToRem(this.props.height);
    }
    // close all poppers ?
  }

  pxToRem(value) {
    return (
      parseInt(value, 10) /
      parseInt(window.getComputedStyle(document.body.parentNode).fontSize.replace('px', ''), 10)
    );
  }

  /**
   * @param {string} activity
   */
  defaultFilter(activity) {
    return {
      activity: activity ? [activity] : [],
      timeOfDay: 'all',
      sportActivityLocation: this.props.sportActivityLocationLock
        ? [this.props.sportActivityLocationLock]
        : [],
    };
  }

  componentDidUpdate() {
    if (typeof this.props.didUpdate === 'function') {
      this.props.didUpdate();
    }
  }

  handleResize() {
    const node = this.timetable;
    if (node && typeof node.getBoundingClientRect === 'function') {
      if (node.getBoundingClientRect().width <= this.calendarMaxWidth) {
        this.setState({
          overrideListType: 'list',
        });
      } else {
        this.setState({
          overrideListType: null,
        });
      }
    }
  }

  componentDidMount() {
    this.resizeFunction = () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => {
        this.handleResize();
      }, 250);
    };
    window.addEventListener('resize', this.resizeFunction);
    this.handleResize();
    this.fetchAndSetActivityList();
    this.getSessions(true);
  }

  componentWillUnmount() {
    clearTimeout(this.resizeTimer);
    window.removeEventListener('resize', this.resizeFunction);
  }

  toggleShowFilters() {
    this.setState({
      showFilters: !this.state.showFilters,
    });
  }

  toggleType() {
    this.setState({
      listType: this.state.listType === 'list' ? 'timetable' : 'list',
    });
  }

  setLoadingCaption = (caption) => {
    this.setState({ loadingCaption: caption });
  };

  onDateChange(date) {
    this.setState(
      {
        date,
      },
      () => {
        this.getSessions();
      }
    );
  }

  onLocationChange = (location) => {
    if (!location) {
      this.setState({
        location,
      });
    } else {
      this.setState(
        {
          location,
        },
        () => {
          this.getSessions();
        }
      );
    }
  };

  onActivityChange(activity) {
    const filter = Object.assign({}, this.state.filter);
    filter.activity = activity;
    this.setState({
      filter,
    });
  }

  onTimeOfDayChange(timeOfDay) {
    const filter = Object.assign({}, this.state.filter);
    filter.timeOfDay = timeOfDay;
    this.setState({
      filter,
    });
  }

  onSportActivityLocationChange(sportActivityLocation) {
    const filter = Object.assign({}, this.state.filter);
    filter.sportActivityLocation = sportActivityLocation;
    this.setState({
      filter,
    });
  }

  /**
   * @param  {...keyof import('./Config').CustomStyle} customStyleSelectionKeys
   */
  getBrandStyle(...customStyleSelectionKeys) {
    const brand =
      accumulateStylePropertiesFromCustomStyleSections(
        this.props.customStyle,
        customStyleSelectionKeys
      ) ?? {};
    if (typeof brand.backgroundColor !== 'undefined') {
      const background = tinycolor(brand.backgroundColor);
      if (background.isValid()) {
        if (typeof brand.color === 'undefined') {
          if (background.isLight()) {
            brand.color = '#000';
          } else {
            brand.color = '#fff';
          }
        }
        if (typeof brand[':hover'] === 'undefined') {
          brand[':hover'] = {};
        }
        if (typeof brand[':hover'].backgroundColor === 'undefined') {
          brand[':hover'].backgroundColor = background.lighten().toString();
        }
      }
    }
    if (
      typeof brand[':hover'] !== 'undefined' &&
      typeof brand[':hover'].backgroundColor !== 'undefined' &&
      typeof brand[':hover'].color === 'undefined'
    ) {
      const hoverBackground = tinycolor(brand[':hover'].backgroundColor);
      if (hoverBackground.isValid()) {
        if (hoverBackground.isLight()) {
          brand[':hover'].color = '#000';
        } else {
          brand[':hover'].color = '#fff';
        }
      }
    }
    return brand;
  }

  /**
   * @param {boolean} [firstRun]
   */
  async getSessions(firstRun) {
    if (!this.state.location || !this.state.date) {
      return;
    }
    const doGetSessions = async () => {
      // Create config object for GetSessions
      const config = {
        timetableWindowStartDate: this.state.date,
        timetableWindowEndDate: DateTime.fromISO(this.state.date)
          .plus({ days: (this.props.numberOfDaysLookahead || this.props.defaultDaysLookahead) - 1 })
          .toISODate(),
      };

      // Create args object for GetSessions
      const args = {
        location: this.props.location || this.state.location,
        provider: this.props.provider,
        activity: this.props.activity,
        iminTag: this.props.iminTag,
        iminTagNot: this.props.iminTagNot,
        showAllSessions: this.props.showAllSessions,
        includeInProgressEvents: this.props.includeInProgressEvents,
      };

      const sessions = await GetSessions.fromCacheOrSearchApi(
        config,
        this.props.apiKey,
        this.state.date,
        args
      );

      this.setState({
        hasLoadedSessions: true,
        sessions,
      });
    };
    if (firstRun) {
      await doGetSessions();
    } else {
      this.setState(
        {
          hasLoadedSessions: false,
        },
        doGetSessions
      );
    }
  }

  async fetchAndSetActivityList() {
    const activityList = await ApiHelper.getActivityList();
    this.setState({
      activityList,
      hasLoadedActivityList: true,
    });
  }

  getListType() {
    return this.state.overrideListType || this.state.listType;
  }

  loadFonts() {
    if (typeof this.props.fonts !== 'undefined' && this.props.fonts instanceof Array) {
      for (const font of this.props.fonts) {
        if (typeof font.cssSrc !== 'undefined') {
          css.insert(`@import url(${font.cssSrc});`);
        } else {
          css.fontFace(font);
        }
      }
    }
  }

  /**
   * @param {HTMLElement} htmlElement
   * @param {number} scale
   * @param {number} height
   */
  async getScreenshot(htmlElement, scale, height) {
    const canvas = await html2canvas(htmlElement, { scale, y: height });
    return canvas.toDataURL('img/png');
  }

  async printPage() {
    if (this.timetable) {
      const doc = new jsPDF({ orientation: 'landscape' });

      const timeTableElement = document.getElementById('timetable_element');
      if (timeTableElement) {
        // Through trail and error, this scale factor seems to best for the embedded timetable
        const scale = 0.75;
        // Add screenshot of page as is
        let height = 0;
        const firstScreenShot = await this.getScreenshot(timeTableElement, scale, height);
        doc.addImage(firstScreenShot, 'JPEG', 0, 0, 0, 0, undefined, undefined, undefined);
        height = height + window.innerHeight / scale;

        // If the timetable requires scrolling, scroll down then take a screenshot
        while (height < timeTableElement?.scrollHeight) {
          const screenshot = await this.getScreenshot(timeTableElement, scale, height);
          doc.addPage('a4', 'landscape');
          doc.addImage(screenshot, 'JPEG', 0, 0, 0, 0, undefined, undefined, undefined);
          height = height + window.innerHeight;
        }

        doc.save(`${this.state.date}.pdf`);
        doc.close();
      }
    }
  }

  isLoading() {
    return !(this.state.hasLoadedActivityList && this.state.hasLoadedSessions);
  }

  render() {
    this.loadFonts();
    const filterStyle = this.getBrandStyle('filter');
    const baseStyle = this.getBrandStyle('base');
    const baseWhitelist = ['fontFamily'];
    if (typeof filterStyle.fontFamily === 'undefined') {
      if (typeof baseStyle.fontFamily === 'undefined') {
        filterStyle.fontFamily = baseStyle.fontFamily;
      }
    }
    css.global(
      '.Timetable .Filter button',
      StyleHelper.applyWhitelistToStyle(filterStyle, baseWhitelist)
    );

    let filterCount = 1;
    let locationFilter;
    if (!this.props.location) {
      filterCount += 1;
      locationFilter = (
        <LocationFilter
          location={this.state.location}
          apiKey={this.props.apiKey}
          provider={this.props.provider}
          onChange={this.onLocationChange}
          setLoadingCaption={this.setLoadingCaption}
        />
      );
    }

    const activityFilter = this.state.showActivityTypes ? (
      <ActivityFilter
        sessions={this.state.sessions}
        filter={this.state.filter.activity}
        onChange={(activity) => this.onActivityChange(activity)}
        isCategoryFilter={this.props.isCategoryFilter}
      />
    ) : (
      ''
    );

    if (activityFilter !== '') {
      filterCount += 1;
    }

    const sportActivityLocationFilter = this.props.showSportActivityLocation ? (
      <SportActivityLocationFilter
        sessions={this.state.sessions}
        filter={this.state.filter.sportActivityLocation}
        onChange={(sportActivityLocation) =>
          this.onSportActivityLocationChange(sportActivityLocation)
        }
        disabled={!!this.props.sportActivityLocationLock}
      />
    ) : (
      ''
    );

    if (sportActivityLocationFilter !== '') {
      filterCount += 1;
    }

    return (
      <div
        id="timetable_element"
        className={classNames({
          Timetable: true,
          SessionsTimetable: true,
          presetLocation: !!this.props.location,
          filterLocation: !this.props.location,
          embedded: !!this.height,
        })}
        style={StyleHelper.applyWhitelistToStyle(baseStyle, baseWhitelist)}
        ref={(timetable) => (this.timetable = timetable)}
      >
        <DayList
          brandStyle={this.getBrandStyle('button', 'dateButton')}
          numberOfDaysLookahead={
            this.props.numberOfDaysLookahead || this.props.defaultDaysLookahead
          }
          visibleWindowSize={this.props.visibleWindowSize}
          arrowStyle={this.props.dayListArrowStyle}
          arrowColor={this.props.dayListArrowColor}
          arrowHoverColor={this.props.dayListArrowHoverColor}
          date={this.state.date}
          onChange={(date) => this.onDateChange(date)}
        />
        <div className="filters">
          <div className="typeToggle">
            <button
              className={this.state.showPrintButton ? 'show' : 'hide'}
              onClick={() => this.printPage()}
            >
              Print Timetable
            </button>
            &nbsp;
            <button
              onClick={() => this.toggleType()}
              aria-label="Toggle view between calendar and list"
            >
              <FontAwesomeIcon icon={this.state.listType === 'list' ? 'table' : 'clock'} />
            </button>
          </div>
          <div className="smallMenu">
            <button onClick={() => this.toggleShowFilters()}>
              <FontAwesomeIcon icon="filter" /> Filter
            </button>
          </div>
          <div className={`subFilters ${this.state.showFilters ? 'showFilters' : ''}`}>
            {locationFilter}
            <TimeOfDayFilter
              sessions={this.state.sessions}
              filter={this.state.filter.timeOfDay}
              onChange={(timeOfDay) => this.onTimeOfDayChange(timeOfDay)}
            />
            {activityFilter}
            {sportActivityLocationFilter}
          </div>
        </div>
        {this.renderContent(filterCount)}
      </div>
    );
  }

  /**
   * @param {number} filterCount
   */
  renderContent(filterCount) {
    if (this.isLoading()) {
      return (
        <div className="Loading">
          <span>{this.state.loadingCaption}</span>
        </div>
      );
    }
    if (!this.state.location) {
      return (
        <div className="NoResults">
          <span>Please select a location to see results</span>
        </div>
      );
    }
    const hasSessions = this.state.sessions && this.state.sessions.length > 0;
    if (hasSessions) {
      if (this.getListType() === 'list') {
        const paddingTop = `${this.state.showFilters ? filterCount * 3.5 + 1 : 0}rem`;
        return (
          <SessionList
            customStyle={this.getBrandStyle('button', 'bookButton')}
            paddingTop={paddingTop}
            height={this.height}
            filter={this.state.filter}
            sessions={this.state.sessions}
            checkoutUrl={this.props.checkoutUrl}
            checkoutTheme={this.props.checkoutTheme}
            didUpdate={this.props.didUpdate}
            bookableProviders={this.props.bookableProviders}
            shouldShowExactAvailability={this.props.shouldShowExactAvailability}
            shouldShowNoAvailability={this.props.shouldShowNoAvailability}
            shouldNotShowDeepLinkButton={this.props.shouldNotShowDeepLinkButton}
            memberDeepLinks={this.props.memberDeepLinks}
            isCategoryFilter={this.props.isCategoryFilter}
            admissionsPolicyUrl={this.props.admissionsPolicyUrl}
            showActivityTypes={this.state.showActivityTypes}
            activityList={this.state.activityList}
            showPrices={this.props.showPrices}
            showSportActivityLocation={this.props.showSportActivityLocation}
            sportActivityLocationLock={this.props.sportActivityLocationLock}
          />
        );
      }
      return (
        <TimetableList
          date={this.state.date}
          height={this.height}
          buttonStyle={this.getBrandStyle('button', 'bookButton')}
          hourStyle={this.getBrandStyle('button', 'hourButton')}
          customStyle={this.getBrandStyle('timetable')}
          filter={this.state.filter}
          sessions={this.state.sessions}
          checkoutUrl={this.props.checkoutUrl}
          checkoutTheme={this.props.checkoutTheme}
          didUpdate={this.props.didUpdate}
          bookableProviders={this.props.bookableProviders}
          shouldShowExactAvailability={this.props.shouldShowExactAvailability}
          shouldShowNoAvailability={this.props.shouldShowNoAvailability}
          shouldNotShowDeepLinkButton={this.props.shouldNotShowDeepLinkButton}
          memberDeepLinks={this.props.memberDeepLinks}
          isCategoryFilter={this.props.isCategoryFilter}
          admissionsPolicyUrl={this.props.admissionsPolicyUrl}
          showActivityTypes={this.state.showActivityTypes}
          activityList={this.state.activityList}
          showPrices={this.props.showPrices}
          showSportActivityLocation={this.props.showSportActivityLocation}
          sportActivityLocationLock={this.props.sportActivityLocationLock}
        />
      );
    }
    return (
      <div className="NoResults">
        <span>
          No sessions returned for current filters. Please try changing one or more filters
        </span>
      </div>
    );
  }
}

Timetable.defaultProps = {
  customStyle: {},
  defaultDaysLookahead: 7,
  fonts: [],
  height: null,
  initialLocation: null,
  location: null,
  showPrices: true,
  showWaitingListBtn: true,
  visibleWindowSize: 7,
  showSportActivityLocation: false,
  sportActivityLocationLock: null,
};

Timetable.propTypes = {
  shouldShowNoAvailability: PropTypes.bool,
  shouldShowExactAvailability: PropTypes.bool,
  shouldNotShowDeepLinkButton: PropTypes.bool,
  memberDeepLinks: PropTypes.object,
  checkoutUrl: PropTypes.string,
  checkoutTheme: PropTypes.string,
  bookableProviders: PropTypes.array,
  iminTag: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  defaultDaysLookahead: PropTypes.number,
  apiKey: PropTypes.string,
  provider: PropTypes.string,
  activity: PropTypes.string,
  fonts: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  location: PropTypes.string,
  initialLocation: PropTypes.string,
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  didUpdate: PropTypes.func,
  iminTagNot: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
  customStyle: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  isCategoryFilter: PropTypes.bool,
  admissionsPolicyUrl: PropTypes.string,
  showActivityTypes: PropTypes.bool,
  showAllSessions: PropTypes.bool,
  includeInProgressEvents: PropTypes.bool,
  showPrintButton: PropTypes.bool,
  showPrices: PropTypes.bool,
  showWaitingListBtn: PropTypes.bool,
  numberOfDaysLookahead: PropTypes.number,
  visibleWindowSize: PropTypes.number,
  dayListArrowStyle: PropTypes.object,
  dayListArrowColor: PropTypes.string,
  dayListArrowHoverColor: PropTypes.string,
  showSportActivityLocation: PropTypes.bool,
  sportActivityLocationLock: PropTypes.oneOfType([PropTypes.string, PropTypes.oneOf([null])]),
};

export default Timetable;
